import { Clone, kStructuredCloneMarker, kSendMarker } from '../../threads/shared/transfer/transfer.js';

const GENERIC_ERROR_MARK = 'CN_GEN_ERR';
/**
 * Базовый класс ошибки. Должен использоваться для создания типизированных ошибок.
 *
 * @export
 * @class GenericError
 * @extends {Error}
 * @implements {ISendableGenericError}
 */
class GenericError extends Error {
    static is(value) {
        if (typeof value !== 'object' || value === null) {
            return false;
        }
        // oxlint-disable-next-line typescript/no-explicit-any
        return value['__mark'] === GENERIC_ERROR_MARK;
    }
    static isLike(value) {
        if (this.is(value)) {
            return true;
        }
        if (typeof value !== 'object' || value === null) {
            return false;
        }
        return value instanceof Error && 'type' in value && 'domain' in value;
    }
    /**
     * Позволяет приводить тип к нужной кастомной ошибке, у которой нет отдельной имплементации
     *
     * @example
     * ```ts
     * interface MyError extends GenericError {
     *  type: 'MyError';
     * }
     *
     * assert.ok(
     *  GenericError.isType<MyError>(new GenericError({ type: 'MyError' }), 'MyError'),
     * );
     * ```
     *
     * @static
     * @template E
     * @param {unknown} value
     * @param {E['type']} type
     * @return {value is E}
     * @memberof GenericError
     */
    static isType(value, type) {
        return GenericError.is(value) && value.type === type;
    }
    static tryFromStructuredClone(err) {
        if (!this.is(err)) {
            return;
        }
        return this.fromStructuredClone(err);
    }
    static fromStructuredClone(err) {
        switch (err.type) {
            default: {
                const { type, domain, details: detailsStruct, message, stack, 
                // oxlint-disable-next-line typescript/no-unused-vars
                __mark, 
                // oxlint-disable-next-line typescript/no-unused-vars
                name, 
                // oxlint-disable-next-line typescript/no-unused-vars
                cause, ...custom } = err;
                let details;
                // TODO: доставать десериализацию нужно из реестра
                if (Clone.is(detailsStruct)) {
                    details = GenericError.tryFromStructuredClone(detailsStruct);
                }
                const genErr = new GenericError({ type, message, details, domain });
                genErr.stack = stack;
                Object.assign(genErr, custom);
                return genErr;
            }
        }
    }
    static restore() {
        return { [kStructuredCloneMarker]: GENERIC_ERROR_MARK, from: this.fromStructuredClone };
    }
    _details = {};
    [kSendMarker] = 1;
    [kStructuredCloneMarker] = GENERIC_ERROR_MARK;
    type;
    domain;
    // oxlint-disable-next-line typescript/ban-ts-comment
    // @ts-ignore
    __mark = GENERIC_ERROR_MARK;
    constructor({ message, type, domain, details }) {
        super(message, { cause: details });
        Object.setPrototypeOf(this, GenericError.prototype);
        this.type = type;
        this.domain = domain;
        this.name = type;
        if (details && !super.cause) {
            this._details = details;
        }
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, GenericError);
        }
        else {
            this.stack = new Error().stack;
        }
    }
    get details() {
        return super.cause || this._details;
    }
    /**
     * Печать детализации ошибки.
     * Внутри происходит сериализация details.
     *
     * @returns string детали ошибки в виде строки
     * @memberof GenericError
     */
    printDetails() {
        return JSON.stringify(this.details, null, 2);
    }
    toStructuredClone() {
        let details;
        if (this.details) {
            if (GenericError.is(this.details)) {
                // TODO: выглядит не надежно
                details = this.details.toStructuredClone();
            }
            else {
                details = this.details;
            }
        }
        return [
            this[kStructuredCloneMarker],
            {
                /** @private */
                __mark: GENERIC_ERROR_MARK,
                type: this.type,
                domain: this.domain,
                message: this.message,
                details,
                stack: this.stack,
                name: this.name,
            },
        ];
    }
    /**
     * Конвертация ошибки в данные.
     * Метод может быть полезен при пробрасывании информации от ошибок более низкого уровня в высокоуровневые ошибки
     */
    toJSON() {
        return {
            type: this.type,
            domain: this.domain,
            message: this.message,
            details: this.details,
        };
    }
}

export { GenericError };
//# sourceMappingURL=genericError.js.map
